import React, {useEffect, useRef, useState} from 'react';
import {
  Image,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import type {CelebrationData, CoachPanelStudent, ManualAwardType} from './types';
import TrophyAsset from '@/Assets/Recognition/Trophy.svg';
import FlashAsset from '@/Assets/Recognition/Flash.svg';
import StarAsset from '@/Assets/Recognition/Star.svg';
import CrownAsset from '@/Assets/Recognition/Crown.svg';
import GreatEffortAsset from '@/Assets/Recognition/GreatEffort.png';
import GoodThinkingAsset from '@/Assets/Recognition/GoodThinking.png';
import OnTimeAsset from '@/Assets/Recognition/OnTime.png';

interface Props {
  visible: boolean;
  initialCelebration: CelebrationData | null;
  classStudents: CoachPanelStudent[];
  hostUserId?: number;
  autoPublishSeconds?: number;
  onPublish: (payload: {
    awardsData: CelebrationData;
    autoAwardAssignments: Record<string, number>;
    extraStars: {studentId: number; count: number}[];
    extraAwards: {studentId: number; awardType: string; count: number}[];
  }) => void;
  onClose: () => void;
}

const C = {
  brown: '#4D3F37', brownLight: '#4D3F37AA',
  bg: '#FAF6EB', cream: '#FFF8E1',
  gold: '#FACF47', goldLight: '#FEF2B5',
  aqua: '#3DAB9E', coral: '#E17B21', purple: '#937ADB',
  green: '#22A06B', greenLight: '#E5F6EE', greenDark: '#1A7F50',
  gray: '#9AA0A6', grayLight: '#F1F2F4', grayBg: '#F8F9FA',
  border: 'rgba(38,35,34,0.14)', white: '#FFFFFF',
};

const AWARD_ASSETS: Record<string, {type: 'img'; src: any} | {type: 'icon'; name: string}> = {
  top_solver:           {type: 'img',  src: TrophyAsset},
  top_performer:        {type: 'img',  src: StarAsset},
  most_active:          {type: 'img',  src: FlashAsset},
  great_effort:         {type: 'img',  src: GreatEffortAsset},
  great_effort_leader:  {type: 'img',  src: GreatEffortAsset},
  fastest_finger:       {type: 'img',  src: FlashAsset},
  good_try:             {type: 'img',  src: StarAsset},
  good_try_leader:      {type: 'img',  src: StarAsset},
  good_thinking:        {type: 'img',  src: GoodThinkingAsset},
  good_thinking_leader: {type: 'img',  src: GoodThinkingAsset},
  focused:              {type: 'icon', name: 'eye'},
  focused_leader:       {type: 'icon', name: 'eye'},
  team_player:          {type: 'img',  src: TrophyAsset},
  team_player_leader:   {type: 'img',  src: TrophyAsset},
  joined_on_time:       {type: 'img',  src: OnTimeAsset},
  mystery_bonus:        {type: 'img',  src: CrownAsset},
};

function AwardImg({awardKey, size, color}: {awardKey: string; size: number; color: string}) {
  const asset = AWARD_ASSETS[awardKey];
  if (!asset) return <Text style={{fontSize: size}}>⭐</Text>;
  if (asset.type === 'icon') return <Ionicons name={asset.name} size={size} color={color} />;
  return <Image source={asset.src} style={{width: size, height: size}} resizeMode="contain" />;
}

// Comparative awards — only meaningful when ≥2 students. Hidden for personal classes.
const COMPARATIVE_AWARDS = new Set([
  'top_solver', 'top_performer', 'most_active', 'fastest_finger',
  'great_effort',
  'good_try_leader', 'great_effort_leader', 'good_thinking_leader',
  'focused_leader', 'team_player_leader',
]);

// All AUTO_AWARDS have unique LABEL strings — no more duplicate "Great Effort ×"
const AUTO_AWARDS: {key: string; label: string; color: string; sub: string}[] = [
  {key: 'top_solver',           label: 'Top Solver',        color: C.gold,    sub: 'most puzzles solved'},
  {key: 'top_performer',        label: 'Top Performer',     color: '#E8A200', sub: 'highest stars'},
  {key: 'most_active',          label: 'Most Active',       color: C.aqua,    sub: 'most attempts'},
  {key: 'fastest_finger',       label: 'Fastest Finger',    color: '#E05FD0', sub: 'quickest correct'},
  {key: 'great_effort',         label: 'Great Effort',      color: C.coral,   sub: 'best effort overall'},
  {key: 'good_try_leader',      label: 'Try Champion',      color: C.aqua,    sub: 'most Good Try badges'},
  {key: 'great_effort_leader',  label: 'Effort Champion',   color: C.coral,   sub: 'most Great Effort badges'},
  {key: 'good_thinking_leader', label: 'Thinking Champion', color: C.gold,    sub: 'most Good Thinking badges'},
  {key: 'focused_leader',       label: 'Focus Champion',    color: C.purple,  sub: 'most Focused badges'},
  {key: 'team_player_leader',   label: 'Team Champion',     color: C.brown,   sub: 'most Team Player badges'},
];

const MANUAL_AWARDS: {key: ManualAwardType; label: string; color: string}[] = [
  {key: 'good_try',      label: 'Good Try',      color: C.aqua},
  {key: 'great_effort',  label: 'Great Effort',  color: C.coral},
  {key: 'good_thinking', label: 'Good Thinking', color: C.gold},
  {key: 'focused',       label: 'Focused',       color: C.purple},
  {key: 'team_player',   label: 'Team Player',   color: C.brown},
];

const BONUS_STAR_OPTIONS = [1, 2, 5];

function playChime() {
  try {
    const ctx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
    [523.25, 659.25, 783.99].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.6);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(start); osc.stop(start + 0.65);
    });
  } catch (e) {}
}

function initials(name: string) {
  return name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
}

// Seed all auto-award slots from celebration data
function buildInitialAssignments(celebration: CelebrationData | null): Record<string, number | null> {
  const map: Record<string, number | null> = {};
  AUTO_AWARDS.forEach(a => { map[a.key] = null; });
  (celebration?.awards ?? []).forEach(a => {
    if (a.award in map) map[a.award] = a.user_id;
  });
  return map;
}

export function EndOfClassConfirmModal({
  visible,
  initialCelebration,
  classStudents,
  hostUserId,
  autoPublishSeconds = 60,
  onPublish,
  onClose,
}: Props) {
  const students = hostUserId
    ? classStudents.filter(s => s.user_id !== hostUserId)
    : classStudents;

  const [autoAssign, setAutoAssign] = useState<Record<string, number | null>>({});
  const [pickerOpen, setPickerOpen] = useState<string | null>(null);
  const [awardCounts, setAwardCounts] = useState<Record<number, Record<string, number>>>({});
  const [extraStars, setExtraStars] = useState<Record<number, number>>({});
  const [countdown, setCountdown] = useState(autoPublishSeconds);
  const [interacted, setInteracted] = useState(false);
  const [bonusOpen, setBonusOpen] = useState(false);
  const [bonusFor, setBonusFor] = useState<number | null>(null);
  const publishedRef = useRef(false);

  useEffect(() => {
    if (!visible) return;
    publishedRef.current = false;
    setInteracted(false);
    setCountdown(autoPublishSeconds);
    setAutoAssign(buildInitialAssignments(initialCelebration));
    setAwardCounts({});
    setExtraStars({});
    setPickerOpen(null);
    setBonusOpen(false);
    setBonusFor(null);
    playChime();
  }, [visible, initialCelebration, autoPublishSeconds]);

  useEffect(() => {
    if (!visible || interacted) return;
    if (countdown <= 0) { doPublish(); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, visible, interacted]);

  const interact = () => { setInteracted(true); };

  const doPublish = () => {
    if (publishedRef.current || !initialCelebration) return;
    publishedRef.current = true;

    const extraStarList = Object.entries(extraStars)
      .filter(([, n]) => n > 0)
      .map(([id, count]) => ({studentId: Number(id), count}));

    const extraAwardList: {studentId: number; awardType: string; count: number}[] = [];
    Object.entries(awardCounts).forEach(([sid, types]) => {
      Object.entries(types).forEach(([awardType, count]) => {
        if (count > 0) extraAwardList.push({studentId: Number(sid), awardType, count});
      });
    });

    const activeAssignments = Object.fromEntries(
      Object.entries(autoAssign)
        .filter(([k, sid]) => sid != null && (!isPersonal || !COMPARATIVE_AWARDS.has(k))),
    ) as Record<string, number>;

    const updatedAwards = Object.entries(activeAssignments).map(([award, user_id]) => {
      const student = students.find(s => s.user_id === user_id);
      return {award: award as any, user_id, name: student?.name ?? ''};
    });

    const preserved = initialCelebration.awards.filter(
      a => a.award === 'everyone_earned' || a.award === 'coach_star_student',
    );

    const updatedAwardsData = {
      ...initialCelebration,
      awards: [...updatedAwards, ...preserved],
    };

    onPublish({awardsData: updatedAwardsData, autoAwardAssignments: activeAssignments, extraStars: extraStarList, extraAwards: extraAwardList});
    // Dismiss after publishing — including the 60s auto-publish. Otherwise this
    // full-screen modal stays mounted and swallows every click (coach can't reach
    // the floating Zoom share/end controls until they close the floating window).
    onClose();
  };

  const adjustStars = (studentId: number, delta: number) => {
    interact();
    setExtraStars(prev => ({...prev, [studentId]: Math.max(0, (prev[studentId] ?? 0) + delta)}));
  };

  const setStarsAbs = (studentId: number, val: number) => {
    interact();
    setExtraStars(prev => ({...prev, [studentId]: Math.max(0, val)}));
  };

  const adjustAward = (studentId: number, awardType: string, delta: number) => {
    interact();
    setAwardCounts(prev => {
      const student = prev[studentId] ?? {};
      const next = Math.max(0, Math.min(2, (student[awardType] ?? 0) + delta));
      return {...prev, [studentId]: {...student, [awardType]: next}};
    });
  };

  const reassignAuto = (awardKey: string, studentId: number | null) => {
    interact();
    setAutoAssign(prev => ({...prev, [awardKey]: studentId}));
    setPickerOpen(null);
  };

  if (!visible) return null;

  const progressPct = Math.max(0, Math.min(100, (countdown / autoPublishSeconds) * 100));
  const isUrgent = countdown <= 10 && !interacted;

  const isPersonal = !!initialCelebration?.is_personal;
  // For personal (1-on-1) classes, comparative awards are meaningless — hide them entirely.
  // Coach still has full access to manual encouragement badges via the bonus accordion.
  const visibleAutoAwards = isPersonal
    ? AUTO_AWARDS.filter(a => !COMPARATIVE_AWARDS.has(a.key))
    : AUTO_AWARDS;
  const goingOut = visibleAutoAwards.filter(a => autoAssign[a.key] != null);
  const notAwarded = visibleAutoAwards.filter(a => autoAssign[a.key] == null);

  const totalExtraStars = Object.values(extraStars).reduce((s, n) => s + n, 0);
  const totalExtraAwards = Object.values(awardCounts).reduce(
    (s, types) => s + Object.values(types).reduce((a, n) => a + n, 0), 0);

  const sorted = [...students].sort((a, b) => b.stars - a.stars);
  const studentsLabel = students.length === 1 ? 'student' : 'students';
  const recipientCount = students.length;

  const bonusBadgeCount = totalExtraStars + totalExtraAwards;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={() => { interact(); onClose(); }}>
      <View style={d.overlay}>
        <View style={d.card}>

          {/* Header */}
          <View style={d.header}>
            <View style={{flex: 1}}>
              <Text style={d.title}>Wrap Up{isPersonal ? ' · 1-on-1' : ''}</Text>
              <Text style={d.subtitle}>
                {isPersonal
                  ? 'Personal session — encouragement awards only'
                  : `${recipientCount} ${studentsLabel} will see their awards`}
              </Text>
            </View>
            {!interacted ? (
              <View style={[d.countdown, isUrgent && d.countdownUrgent]}>
                <Text style={d.countdownText}>{countdown}s</Text>
              </View>
            ) : (
              <View style={d.editingTag}>
                <Text style={d.editingText}>editing</Text>
              </View>
            )}
          </View>

          {/* Progress bar — only when auto-publish active */}
          {!interacted && (
            <View style={d.progressTrack}>
              <View style={[d.progressFill, {width: `${progressPct}%` as any}, isUrgent && d.progressUrgent]} />
            </View>
          )}

          {/* Scrollable body */}
          <ScrollView style={d.body} contentContainerStyle={{paddingBottom: 12}}>

            {/* GOING OUT */}
            {goingOut.length > 0 && (
              <View style={d.section}>
                <View style={d.sectionHead}>
                  <Text style={[d.sectionLabel, {color: C.green}]}>✓ GOING OUT</Text>
                  <Text style={[d.sectionCount, {color: C.green}]}>{goingOut.length}</Text>
                </View>
                <View style={d.awardList}>
                  {goingOut.map(award => (
                    <AwardRow
                      key={award.key}
                      award={award}
                      assigned={true}
                      assignedStudent={students.find(s => s.user_id === autoAssign[award.key])}
                      students={students}
                      pickerOpen={pickerOpen === award.key}
                      onTogglePicker={() => { setInteracted(true); setPickerOpen(pickerOpen === award.key ? null : award.key); }}
                      onAssign={(sid) => reassignAuto(award.key, sid)}
                    />
                  ))}
                </View>
              </View>
            )}

            {/* NOT AWARDED */}
            {notAwarded.length > 0 && (
              <View style={d.section}>
                <View style={d.sectionHead}>
                  <Text style={[d.sectionLabel, {color: C.gray}]}>✗ NOT AWARDED</Text>
                  <Text style={[d.sectionCount, {color: C.gray}]}>{notAwarded.length}</Text>
                </View>
                {goingOut.length === 0 && (
                  <Text style={d.emptyHint}>No auto-winners detected. Tap any below to recognize a student.</Text>
                )}
                <View style={d.awardList}>
                  {notAwarded.map(award => (
                    <AwardRow
                      key={award.key}
                      award={award}
                      assigned={false}
                      assignedStudent={undefined}
                      students={students}
                      pickerOpen={pickerOpen === award.key}
                      onTogglePicker={() => { setInteracted(true); setPickerOpen(pickerOpen === award.key ? null : award.key); }}
                      onAssign={(sid) => reassignAuto(award.key, sid)}
                    />
                  ))}
                </View>
              </View>
            )}

            {/* BONUS ACCORDION */}
            <TouchableOpacity
              style={d.accordionHead}
              onPress={() => { setInteracted(true); setBonusOpen(!bonusOpen); }}>
              <Text style={d.accordionCaret}>{bonusOpen ? '▼' : '▶'}</Text>
              <Text style={d.accordionLabel}>Give bonus stars to anyone</Text>
              <Text style={d.accordionHint}>(optional)</Text>
              {bonusBadgeCount > 0 && (
                <View style={d.accordionBadge}>
                  <Text style={d.accordionBadgeText}>
                    {totalExtraStars > 0 ? `+${totalExtraStars}⭐` : ''}
                    {totalExtraStars > 0 && totalExtraAwards > 0 ? '  ' : ''}
                    {totalExtraAwards > 0 ? `${totalExtraAwards} 🎖` : ''}
                  </Text>
                </View>
              )}
            </TouchableOpacity>

            {bonusOpen && (
              <View style={d.bonusList}>
                {sorted.map(s => {
                  const extra = extraStars[s.user_id] ?? 0;
                  const pending = awardCounts[s.user_id] ?? {};
                  const earned = s.manual_award_counts ?? {};
                  const pendingBadges = Object.values(pending).reduce((a, b) => a + b, 0);
                  // Merge earned + pending per badge type
                  const merged: {key: string; total: number; pending: number; color: string; label: string}[] = [];
                  MANUAL_AWARDS.forEach(a => {
                    const e = earned[a.key] ?? 0;
                    const p = pending[a.key] ?? 0;
                    if (e + p > 0) merged.push({key: a.key, total: e + p, pending: p, color: a.color, label: a.label});
                  });
                  const isZero = s.stars === 0 && extra === 0 && merged.length === 0;
                  return (
                    <View key={s.user_id} style={[d.bonusRow, isZero && d.bonusRowZero]}>
                      <View style={d.bonusRowMain}>
                        <View style={[d.bonusAvatar, isZero && d.bonusAvatarZero]}>
                          <Text style={d.bonusAvatarText}>{initials(s.name)}</Text>
                        </View>
                        <View style={{flex: 1, minWidth: 0}}>
                          <Text style={d.bonusName} numberOfLines={1}>{s.name}</Text>
                          <Text style={[d.bonusGiven, isZero && {color: C.gray}]}>
                            {extra > 0 && <Text style={{color: C.gold, fontWeight: '800'}}>+{extra}⭐  </Text>}
                            <Text style={{color: C.brownLight}}>★ {s.stars + extra}</Text>
                            {pendingBadges > 0 && (
                              <Text style={{color: C.aqua, fontWeight: '700'}}>{`  · +${pendingBadges} new`}</Text>
                            )}
                          </Text>
                        </View>
                        <TouchableOpacity
                          onPress={() => { setInteracted(true); setBonusFor(s.user_id); }}
                          style={d.bonusAddBtn}>
                          <Text style={d.bonusAddText}>+ bonus</Text>
                        </TouchableOpacity>
                      </View>
                      {merged.length > 0 && (
                        <View style={d.bonusBadgeRow}>
                          {merged.map(m => (
                            <View
                              key={m.key}
                              style={[
                                d.bonusBadgeChip,
                                {borderColor: m.color + '55', backgroundColor: m.color + '15'},
                                m.pending > 0 && {borderWidth: 1.5, borderColor: m.color},
                              ]}>
                              <AwardImg awardKey={m.key} size={11} color={m.color} />
                              <Text style={[d.bonusBadgeCount, {color: m.color}]}>×{m.total}</Text>
                              {m.pending > 0 && (
                                <View style={[d.bonusBadgePending, {backgroundColor: m.color}]}>
                                  <Text style={d.bonusBadgePendingText}>+{m.pending}</Text>
                                </View>
                              )}
                            </View>
                          ))}
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            )}

          </ScrollView>

          {/* Footer */}
          <View style={d.footer}>
            <TouchableOpacity onPress={() => { interact(); onClose(); }} style={d.cancelBtn}>
              <Text style={d.cancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => { interact(); doPublish(); }} style={d.publishBtn}>
              <Text style={d.publishText}>Publish to {recipientCount} 🎉</Text>
            </TouchableOpacity>
          </View>

          {/* Bonus popover */}
          {bonusFor != null && (() => {
            const s = students.find(st => st.user_id === bonusFor);
            if (!s) return null;
            const extra = extraStars[bonusFor] ?? 0;
            const counts = awardCounts[bonusFor] ?? {};
            return (
              <View style={d.popoverScrim}>
                <TouchableOpacity style={StyleSheet.absoluteFill} onPress={() => setBonusFor(null)} />
                <View style={d.popoverCard}>
                  <View style={d.popoverHead}>
                    <Text style={d.popoverTitle}>Bonus for {s.name.split(' ')[0]}</Text>
                    <TouchableOpacity onPress={() => setBonusFor(null)} style={d.popoverClose}>
                      <Text style={{fontSize: 16, color: C.brownLight}}>✕</Text>
                    </TouchableOpacity>
                  </View>

                  <Text style={d.popoverSection}>BONUS STARS</Text>
                  <View style={d.popoverStarRow}>
                    {BONUS_STAR_OPTIONS.map(n => (
                      <TouchableOpacity
                        key={n}
                        onPress={() => setStarsAbs(bonusFor, extra + n)}
                        style={d.popoverStarBtn}>
                        <Text style={d.popoverStarText}>+{n}⭐</Text>
                      </TouchableOpacity>
                    ))}
                    {extra > 0 && (
                      <TouchableOpacity
                        onPress={() => setStarsAbs(bonusFor, 0)}
                        style={d.popoverResetBtn}>
                        <Text style={d.popoverResetText}>reset</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                  {extra > 0 && (
                    <Text style={d.popoverCurrent}>Added: <Text style={{color: C.gold, fontWeight: '800'}}>+{extra}⭐</Text></Text>
                  )}

                  <Text style={[d.popoverSection, {marginTop: 14}]}>BADGE</Text>
                  <View style={d.popoverBadgeRow}>
                    {MANUAL_AWARDS.map(a => {
                      const cnt = counts[a.key] ?? 0;
                      const capped = cnt >= 2;
                      return (
                        <TouchableOpacity
                          key={a.key}
                          onPress={() => !capped && adjustAward(bonusFor, a.key, 1)}
                          disabled={capped}
                          style={[d.popoverBadge, {borderColor: a.color + '88'}, capped && d.popoverBadgeCapped, cnt > 0 && {backgroundColor: a.color + '15'}]}>
                          <AwardImg awardKey={a.key} size={16} color={a.color} />
                          <Text style={[d.popoverBadgeLabel, {color: a.color}]}>{a.label}</Text>
                          {cnt > 0 && (
                            <View style={[d.popoverBadgeCount, {backgroundColor: a.color}]}>
                              <Text style={d.popoverBadgeCountText}>×{cnt}</Text>
                            </View>
                          )}
                          {capped && (
                            <Text style={d.popoverBadgeCapText}>max</Text>
                          )}
                        </TouchableOpacity>
                      );
                    })}
                  </View>

                  <View style={d.popoverFooter}>
                    {(extra > 0 || Object.values(counts).some(n => n > 0)) && (
                      <TouchableOpacity
                        onPress={() => {
                          setStarsAbs(bonusFor, 0);
                          MANUAL_AWARDS.forEach(a => adjustAward(bonusFor, a.key, -counts[a.key] ?? 0));
                        }}
                        style={d.popoverClearAll}>
                        <Text style={d.popoverClearAllText}>Clear all</Text>
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity onPress={() => setBonusFor(null)} style={d.popoverDone}>
                      <Text style={d.popoverDoneText}>Done</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            );
          })()}

        </View>
      </View>
    </Modal>
  );
}

// ─── AwardRow subcomponent ────────────────────────────────────────────
function AwardRow({
  award, assigned, assignedStudent, students, pickerOpen, onTogglePicker, onAssign,
}: {
  award: {key: string; label: string; color: string; sub: string};
  assigned: boolean;
  assignedStudent: CoachPanelStudent | undefined;
  students: CoachPanelStudent[];
  pickerOpen: boolean;
  onTogglePicker: () => void;
  onAssign: (sid: number | null) => void;
}) {
  return (
    <View style={[d.awardRow, assigned && d.awardRowAssigned]}>
      <TouchableOpacity onPress={onTogglePicker} style={d.awardRowMain}>
        <View style={[d.awardIcon, {backgroundColor: award.color + '20'}]}>
          <AwardImg awardKey={award.key} size={18} color={award.color} />
        </View>
        <View style={{flex: 1, minWidth: 0}}>
          <Text style={[d.awardLabel, {color: assigned ? C.brown : C.brownLight}]} numberOfLines={1}>
            {award.label}
          </Text>
          <Text style={[d.awardSub, {color: assigned ? award.color : C.gray}]} numberOfLines={1}>
            {assignedStudent ? assignedStudent.name : award.sub}
          </Text>
        </View>
        <View style={[d.awardAction, {backgroundColor: assigned ? award.color + '18' : C.grayLight}]}>
          <Text style={[d.awardActionText, {color: assigned ? award.color : C.brownLight}]}>
            {assigned ? 'change' : 'assign'}{' '}
            {pickerOpen ? '▴' : '▾'}
          </Text>
        </View>
      </TouchableOpacity>

      {pickerOpen && (
        <View style={d.picker}>
          <ScrollView style={{maxHeight: 180}} nestedScrollEnabled>
            {assignedStudent && (
              <TouchableOpacity style={d.pickerRow} onPress={() => onAssign(null)}>
                <Text style={[d.pickerRowText, {color: C.coral}]}>— Leave unassigned —</Text>
              </TouchableOpacity>
            )}
            {students.map(s => {
              const selected = assignedStudent?.user_id === s.user_id;
              return (
                <TouchableOpacity key={s.user_id} style={[d.pickerRow, selected && {backgroundColor: award.color + '12'}]} onPress={() => onAssign(s.user_id)}>
                  <Text style={[d.pickerRowText, selected && {color: award.color, fontWeight: '700'}]}>
                    {selected ? '✓ ' : ''}{s.name}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

const d = StyleSheet.create({
  overlay: {flex: 1, backgroundColor: 'rgba(15,15,15,0.6)', alignItems: 'center', justifyContent: 'center', padding: 20},
  card: {backgroundColor: C.white, borderRadius: 18, width: '100%', maxWidth: 560, maxHeight: '94%', borderWidth: 1, borderColor: C.border, overflow: 'hidden', shadowColor: '#000', shadowOpacity: 0.25, shadowRadius: 24, shadowOffset: {width: 0, height: 8}, elevation: 8},

  header: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 18, paddingTop: 16, paddingBottom: 8},
  title: {fontSize: 20, fontWeight: '800', color: C.brown},
  subtitle: {fontSize: 13, color: C.brownLight, marginTop: 2},
  countdown: {minWidth: 50, height: 36, paddingHorizontal: 10, borderRadius: 18, backgroundColor: C.gold, alignItems: 'center', justifyContent: 'center'},
  countdownUrgent: {backgroundColor: C.coral},
  countdownText: {fontSize: 14, fontWeight: '900', color: C.white},
  editingTag: {paddingHorizontal: 10, height: 28, backgroundColor: C.greenLight, borderRadius: 14, alignItems: 'center', justifyContent: 'center'},
  editingText: {fontSize: 11, fontWeight: '800', color: C.greenDark, textTransform: 'uppercase', letterSpacing: 0.5},

  progressTrack: {height: 3, backgroundColor: C.goldLight, marginHorizontal: 18, borderRadius: 2, overflow: 'hidden'},
  progressFill: {height: '100%', backgroundColor: C.gold},
  progressUrgent: {backgroundColor: C.coral},

  body: {flex: 1, marginTop: 8},

  section: {paddingHorizontal: 18, paddingTop: 12},
  sectionHead: {flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8},
  sectionLabel: {fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 1.2},
  sectionCount: {fontSize: 11, fontWeight: '800', backgroundColor: C.grayLight, paddingHorizontal: 7, paddingVertical: 1, borderRadius: 10, overflow: 'hidden'},
  emptyHint: {fontSize: 12, color: C.brownLight, marginBottom: 8, fontStyle: 'italic'},

  awardList: {gap: 6},
  awardRow: {borderRadius: 10, backgroundColor: C.grayBg, overflow: 'hidden'},
  awardRowAssigned: {backgroundColor: C.greenLight + '88'},
  awardRowMain: {flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10},
  awardIcon: {width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center'},
  awardLabel: {fontSize: 13, fontWeight: '700'},
  awardSub: {fontSize: 12, marginTop: 1, fontWeight: '600'},
  awardAction: {paddingHorizontal: 10, paddingVertical: 5, borderRadius: 12},
  awardActionText: {fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.5},

  picker: {backgroundColor: C.white, borderTopWidth: 1, borderTopColor: C.border},
  pickerRow: {paddingHorizontal: 14, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: C.border},
  pickerRowText: {fontSize: 13, color: C.brown},

  accordionHead: {flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 18, paddingVertical: 14, marginTop: 12, backgroundColor: C.cream, borderTopWidth: 1, borderBottomWidth: 1, borderColor: C.gold + '44'},
  accordionCaret: {fontSize: 12, color: C.brown, fontWeight: '800'},
  accordionLabel: {fontSize: 13, fontWeight: '800', color: C.brown},
  accordionHint: {fontSize: 11, color: C.brownLight, flex: 1},
  accordionBadge: {backgroundColor: C.gold, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10},
  accordionBadgeText: {fontSize: 11, fontWeight: '800', color: C.brown},

  bonusList: {paddingHorizontal: 14, paddingTop: 8, gap: 6},
  bonusRow: {paddingHorizontal: 10, paddingVertical: 8, borderRadius: 10, backgroundColor: C.white, borderWidth: 1, borderColor: C.border, gap: 6},
  bonusRowMain: {flexDirection: 'row', alignItems: 'center', gap: 10},
  bonusRowZero: {borderColor: C.coral + '44', backgroundColor: C.coral + '08'},
  bonusAvatar: {width: 30, height: 30, borderRadius: 15, backgroundColor: C.bg, alignItems: 'center', justifyContent: 'center'},
  bonusAvatarZero: {backgroundColor: C.coral + '20'},
  bonusAvatarText: {fontSize: 11, fontWeight: '800', color: C.brown},
  bonusName: {fontSize: 13, fontWeight: '700', color: C.brown},
  bonusGiven: {fontSize: 11, fontWeight: '600', color: C.aqua, marginTop: 1},
  bonusTotal: {fontSize: 12, fontWeight: '800'},
  bonusAddBtn: {paddingHorizontal: 10, paddingVertical: 6, borderRadius: 10, backgroundColor: C.gold},
  bonusAddText: {fontSize: 11, fontWeight: '800', color: C.brown},
  // Earned-badge chips inside bonus row (per-badge counts)
  bonusBadgeRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 4, paddingLeft: 40},
  bonusBadgeChip: {flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 6, paddingVertical: 3, borderRadius: 10, borderWidth: 1, position: 'relative'},
  bonusBadgeCount: {fontSize: 10, fontWeight: '800', fontVariant: ['tabular-nums']},
  bonusBadgePending: {position: 'absolute', top: -5, right: -5, paddingHorizontal: 4, paddingVertical: 1, borderRadius: 7, minWidth: 14, alignItems: 'center'},
  bonusBadgePendingText: {fontSize: 8, fontWeight: '800', color: C.white, fontVariant: ['tabular-nums']},

  footer: {flexDirection: 'row', gap: 10, justifyContent: 'flex-end', alignItems: 'center', paddingHorizontal: 18, paddingVertical: 14, borderTopWidth: 1, borderTopColor: C.border, backgroundColor: C.white},
  cancelBtn: {paddingHorizontal: 18, paddingVertical: 10, borderWidth: 1, borderColor: C.border, borderRadius: 12},
  cancelText: {fontSize: 13, fontWeight: '700', color: C.brown},
  publishBtn: {paddingHorizontal: 22, paddingVertical: 11, borderRadius: 12, backgroundColor: C.aqua},
  publishText: {fontSize: 14, fontWeight: '800', color: C.white},

  popoverScrim: {position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', alignItems: 'center', justifyContent: 'center', padding: 16},
  popoverCard: {backgroundColor: C.white, borderRadius: 14, width: '100%', maxWidth: 380, padding: 16, shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: 16, shadowOffset: {width: 0, height: 4}},
  popoverHead: {flexDirection: 'row', alignItems: 'center', marginBottom: 12},
  popoverTitle: {flex: 1, fontSize: 16, fontWeight: '800', color: C.brown},
  popoverClose: {padding: 4},
  popoverSection: {fontSize: 10, fontWeight: '800', color: C.brownLight, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8},
  popoverStarRow: {flexDirection: 'row', gap: 8, alignItems: 'center'},
  popoverStarBtn: {flex: 1, paddingVertical: 10, borderRadius: 10, borderWidth: 1.5, borderColor: C.gold, backgroundColor: C.goldLight + '66', alignItems: 'center'},
  popoverStarText: {fontSize: 14, fontWeight: '800', color: C.brown},
  popoverResetBtn: {paddingHorizontal: 12, paddingVertical: 10},
  popoverResetText: {fontSize: 12, fontWeight: '700', color: C.coral},
  popoverCurrent: {fontSize: 12, color: C.brownLight, marginTop: 8},
  popoverBadgeRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 6},
  popoverBadge: {flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 7, borderRadius: 18, borderWidth: 1.5, position: 'relative'},
  popoverBadgeCapped: {opacity: 0.4},
  popoverBadgeLabel: {fontSize: 12, fontWeight: '700'},
  popoverBadgeCount: {minWidth: 18, height: 18, borderRadius: 9, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4},
  popoverBadgeCountText: {fontSize: 10, fontWeight: '800', color: C.white},
  popoverBadgeCapText: {fontSize: 9, fontWeight: '800', color: C.coral, textTransform: 'uppercase'},
  popoverFooter: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, gap: 10},
  popoverClearAll: {paddingHorizontal: 10, paddingVertical: 7},
  popoverClearAllText: {fontSize: 12, fontWeight: '700', color: C.brownLight},
  popoverDone: {flex: 1, paddingVertical: 11, borderRadius: 10, backgroundColor: C.aqua, alignItems: 'center'},
  popoverDoneText: {fontSize: 14, fontWeight: '800', color: C.white},
});
